// src/components/doctor-profile/AboutSection.jsx
import React, { useMemo, useState } from "react";
import { User, GraduationCap } from "lucide-react";
import { styles as baseStyles } from "../../style/DoctorProfile.styles";

/**
 * AboutSection — premium editorial style
 * - Soft fade clamp (no mid-word cut) with smooth expand/collapse
 * - Smart bullets, blockquotes, autolink URLs, tasteful hashtag chips
 * - Desktop-first typography & spacing (centered, readable width)
 */
const AboutSection = ({ doctor = {}, styles = baseStyles, clampChars = 700 }) => {
  const shortLastName = (doctor?.name || "Doctor").split(" ").pop();

  /** ---------- Source text normalization ---------- */
  const normalized = useMemo(() => {
    return String(doctor?.bio || "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }, [doctor?.bio]);

  const paragraphs = useMemo(
    () => (normalized ? normalized.split("\n\n").map((p) => p.trim()).filter(Boolean) : []),
    [normalized]
  );

  /** ---------- Render helpers ---------- */
  const urlRegex = /(https?:\/\/[^\s)]+)|(www\.[^\s)]+)/gi;
  const hashRegex = /(^|\s)(#[\p{L}\p{N}_]+)/giu;

  const linkify = (text) =>
    text.split(urlRegex).map((chunk, i) => {
      if (!chunk) return null;
      const isUrl = /^(https?:\/\/|www\.)/i.test(chunk);
      if (!isUrl) return <React.Fragment key={i}>{chunk}</React.Fragment>;
      const href = chunk.startsWith("http") ? chunk : `https://${chunk}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "#0f172a",
            textDecoration: "underline",
            textDecorationColor: "rgba(37,99,235,.35)",
            textUnderlineOffset: "3px",
            fontWeight: 600,
          }}
        >
          {chunk}
        </a>
      );
    });

  const hashify = (node, keyBase = "h") => {
    if (typeof node !== "string") return node;
    const parts = [];
    let last = 0;
    for (const m of node.matchAll(hashRegex)) {
      const [full, space, tag] = m;
      const start = m.index + (space ? space.length : 0);
      if (m.index > last) parts.push(node.slice(last, m.index));
      if (space) parts.push(space);
      parts.push(
        <span
          key={`${keyBase}-${start}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#eef2ff",
            color: "#1e3a8a",
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: "0.92em",
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {tag}
        </span>
      );
      last = start + tag.length;
    }
    if (last < node.length) parts.push(node.slice(last));
    return parts.length ? parts : node;
  };

  const styleInline = (text, key) => {
    const linked = linkify(text);
    const withHashes = linked.map((n, i) => (typeof n === "string" ? hashify(n, `${key}-${i}`) : n));
    return <>{withHashes}</>;
  };

  const isBullet = (line) => {
    const t = line.trim();
    if (!t) return false;
    if (/^[-•]\s+/.test(t)) return true;
    return /^\p{Extended_Pictographic}/u.test(t);
  };

  const isQuote = (line) => /^>\s?/.test(line.trim());

  const renderParagraph = (p, idx) => {
    const lines = p.split("\n").filter(Boolean);
    const allBullets = lines.length > 1 && lines.every(isBullet);
    const allQuotes = lines.length >= 1 && lines.every(isQuote);

    if (allBullets) {
      return (
        <ul
          key={`ul-${idx}`}
          style={{
            listStyle: "none",
            margin: "0 0 16px 0",
            padding: 0,
          }}
        >
          {lines.map((l, i) => {
            const cleaned = l.replace(/^[-•]\s+/, "").trim();
            return (
              <li
                key={`li-${idx}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "10px 1fr",
                  gap: 10,
                  padding: "8px 0",
                  color: "#334155",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    marginTop: 8,
                    borderRadius: "50%",
                    background: "#93c5fd",
                  }}
                />
                <span style={{ lineHeight: 1.8, wordBreak: "break-word" }}>
                  {styleInline(cleaned, `b-${idx}-${i}`)}
                </span>
              </li>
            );
          })}
        </ul>
      );
    }

    if (allQuotes) {
      return (
        <blockquote
          key={`q-${idx}`}
          style={{
            margin: "0 0 16px 0",
            padding: "10px 14px",
            borderLeft: "4px solid #c7d2fe",
            background: "#f8fafc",
            color: "#334155",
            fontStyle: "italic",
          }}
        >
          {lines.map((l, i) => {
            const cleaned = l.replace(/^>\s?/, "");
            return (
              <p key={`qp-${idx}-${i}`} style={{ margin: i ? "8px 0 0 0" : 0, lineHeight: 1.8 }}>
                {styleInline(cleaned, `q-${idx}-${i}`)}
              </p>
            );
          })}
        </blockquote>
      );
    }

    return (
      <p
        key={`p-${idx}`}
        style={{
          margin: "0 0 16px 0",
          color: "#334155",
          lineHeight: 1.9,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {styleInline(p, `p-${idx}`)}
      </p>
    );
  };

  /** ---------- Clamp (w/ fade) ---------- */
  const fullText = useMemo(() => paragraphs.join("\n\n"), [paragraphs]);
  const needsClamp = fullText.length > clampChars;
  const [expanded, setExpanded] = useState(false);

  const clampedParas = useMemo(() => {
    if (!needsClamp || expanded) return paragraphs;
    let count = 0;
    const out = [];
    for (const p of paragraphs) {
      if (count + p.length <= clampChars) {
        out.push(p);
        count += p.length + 2;
      } else {
        // softly trim to last space for cleaner edge
        const remain = Math.max(0, clampChars - count);
        const slice = p.slice(0, remain);
        const lastSpace = slice.lastIndexOf(" ");
        const clean = (lastSpace > 60 ? slice.slice(0, lastSpace) : slice).trimEnd() + "…";
        out.push(clean);
        break;
      }
    }
    return out;
  }, [paragraphs, needsClamp, expanded, clampChars]);

  /** ---------- Education list ---------- */
  const educationItems = useMemo(() => {
    const s = String(doctor?.education || "").trim();
    if (!s) return [];
    return s
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
  }, [doctor?.education]);

  return (
    <section style={{ ...styles.section }}>
      {/* Section header */}
      <div
        style={{
          ...styles.sectionHeader,
          gap: 10,
          borderBottom: "1px solid #e5e7eb",
          background: "#f8fafc",
        }}
        className="profile-header"
      >
        <User style={{ width: 18, height: 18, color: "#2563eb" }} />
        <h2 style={{ ...styles.sectionTitle, margin: 0, fontSize: 18, letterSpacing: ".2px" }}>
          About Dr. {shortLastName}
        </h2>
      </div>

      <div className="section-content" style={{ ...styles.sectionContent, paddingTop: 18 }}>
        {/* BIO */}
        {paragraphs.length ? (
          <div
            style={{
              margin: "0 auto",
              maxWidth: 900, // wider but still readable on desktop
              fontSize: 15.5,
              textAlign: "left",
              position: "relative",
              transition: "max-height .25s ease",
            }}
          >
            {clampedParas.map((p, i) => renderParagraph(p, i))}

            {/* Fade overlay only when clamped & not expanded */}
            {needsClamp && !expanded && (
              <div
                aria-hidden
                style={{
                  position: "relative",
                  height: 48,
                  marginTop: -48,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.9) 40%, rgba(255,255,255,1) 100%)",
                }}
              />
            )}

            {needsClamp && (
              <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="focus-ring"
                  style={{
                    appearance: "none",
                    border: "1px solid #c7d2fe",
                    background: expanded ? "#eef2ff" : "#eff6ff",
                    color: "#1e3a8a",
                    fontWeight: 800,
                    padding: "8px 12px",
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "all .15s ease",
                  }}
                >
                  {expanded ? "Show less" : "Read more"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <User style={{ width: 40, height: 40, margin: "0 auto 10px auto", color: "#94a3b8" }} />
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>No biography available</p>
          </div>
        )}

        {/* EDUCATION */}
        {!!educationItems.length && (
          <div style={{ marginTop: 22 }}>
            <h3
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                letterSpacing: ".15px",
              }}
            >
              <GraduationCap style={{ width: 16, height: 16, color: "#2563eb" }} />
              Education & Training
            </h3>

            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {educationItems.map((item, i) => (
                <li
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "12px 1fr",
                    gap: 12,
                    alignItems: "flex-start",
                    padding: "10px 14px",
                    background: i % 2 ? "#fafafa" : "#fff",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      marginTop: 8,
                      borderRadius: "50%",
                      background: "#93c5fd",
                    }}
                  />
                  <span style={{ color: "#334155", lineHeight: 1.7 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutSection;
